import { useLocalObservable } from 'mobx-react-lite';
import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useTranslation } from '@gem/modules/i18n';
import { popupsTasks } from '@gem/popups';

import Tasks from 'APP/Tasks';
import { ICalendarAccount } from 'APP/model/calendar/calendar.types';
import { RouterLink } from 'APP/router/constants';
import Entities from 'STORE';

interface ICalendarPresenter {
  initialize(): Promise<void>;
  checkAuthorizationAccounts(): Promise<void>;
  showAccountConnectionStatus(status: string): void;
  activeCalendarAccounts: ICalendarAccount[];
  isActiveAccount: boolean;
  isLoading: boolean;
  isCompactViewEnabled: boolean;
}

export const useCalendarPresenter = (): ICalendarPresenter => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const urlSearchParams = new URLSearchParams(location.search);
  const queryParams = Object.fromEntries(urlSearchParams.entries());

  const presenter = useLocalObservable<ICalendarPresenter>(() => ({
    isAccountsRequested: false,

    get isLoading(): boolean {
      return Entities.calendar.isLoading;
    },

    get activeCalendarAccounts(): ICalendarAccount[] {
      return Entities.calendar.activeCalendarAccounts;
    },

    get isActiveAccount(): boolean {
      return Entities.calendar.isActiveAccount;
    },

    get isCompactViewEnabled(): boolean {
      return Entities.calendar.isCompactViewEnabled;
    },

    showAccountConnectionStatus(status: string): void {
      if (!status) {
        return;
      }

      if (status === 'success') {
        popupsTasks.showToast(t('calendar_toast_google_connected'));
      }

      if (status === 'failed') {
        popupsTasks.showToast(t('calendar_toast_google_connect_failed'), {
          style: '4',
        });
      }

      navigate(`${RouterLink.Calendar}`, {
        replace: true,
      });
    },

    async initialize(): Promise<void> {
      Tasks.calendar.initialize();
    },

    async checkAuthorizationAccounts(): Promise<void> {
      await Tasks.calendar.checkAuthorizationAccounts();
    },
  }));

  useEffect(() => {
    presenter.checkAuthorizationAccounts();
  }, []);

  useEffect(() => {
    if (queryParams.result) {
      presenter.showAccountConnectionStatus(queryParams.result);
    } else {
      presenter.checkAuthorizationAccounts();
    }
  }, [queryParams.result]);

  useEffect(() => {
    if (presenter.isActiveAccount) {
      presenter.initialize();
    }
  }, [presenter.isActiveAccount]);

  useEffect(() => {
    Entities.calendar.increaseCalendarInstancesCount();
    return () => {
      Entities.calendar.decreaseCalendarInstancesCount();
    };
  }, []);

  return presenter;
};
